import { Check, Mail, MessageCircleX, Pin, PinOff, Undo2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { TransferPerson } from './lib/transfer-icon';
import type { TicketStatus } from './types';

/**
 * Quais ações o menu do card oferece, e em que ordem.
 *
 * Separado do componente porque é decisão, não desenho: a ordem foi definida
 * pelo dono e muda o que o atendente acerta no susto. A sequência é o caminho
 * da conversa saindo das mãos dele — passar adiante, tirar de mim, marcar pra
 * depois, fixar — e o destrutivo por último, longe do cursor que acabou de
 * abrir o menu.
 */

export interface ItemDoMenu {
  key: string;
  icon: LucideIcon;
  label: string;
  onClick: () => void;
  danger?: boolean;
}

export interface AcoesDoCard {
  status: TicketStatus;
  onAccept: () => void;
  onQueue: () => void;
  onTransfer: () => void;
  onClose: () => void;
  onReopen: () => void;
  /** Conversa já fixada no topo (muda o rótulo pra "Desafixar"). */
  pinned?: boolean | undefined;
  /** Ausente fora da aba Ativos — fixar só existe lá. */
  onTogglePin?: (() => void) | undefined;
  /** Ausente quando o atendimento JÁ está não-lido: nada a fazer. */
  onMarkUnread?: (() => void) | undefined;
}

export function montarItensDoMenu(a: AcoesDoCard): ItemDoMenu[] {
  // Encerrado só volta pra fila: transferir, marcar e fixar não dizem nada
  // sobre uma conversa que já terminou.
  if (a.status === 'closed') {
    return [{ key: 'reopen', icon: Undo2, label: 'Retornar para Fila', onClick: a.onReopen }];
  }

  // Em espera, aceitar vem na FRENTE de tudo (pedido do dono): é o que o
  // atendente faz em 9 de 10 vezes ao abrir o menu de um atendimento sem dono.
  // Em atendimento meu, o topo é passar adiante e só então devolver pra fila.
  const itens: ItemDoMenu[] =
    a.status === 'pending'
      ? [
          { key: 'accept', icon: Check, label: 'Aceitar conversa', onClick: a.onAccept },
          { key: 'transfer', icon: TransferPerson, label: 'Transferir', onClick: a.onTransfer },
        ]
      : [
          { key: 'transfer', icon: TransferPerson, label: 'Transferir', onClick: a.onTransfer },
          { key: 'queue', icon: Undo2, label: 'Retornar para Fila', onClick: a.onQueue },
        ];

  if (a.onMarkUnread) {
    itens.push({ key: 'unread', icon: Mail, label: 'Marcar como não lida', onClick: a.onMarkUnread });
  }
  if (a.onTogglePin) {
    itens.push({
      key: 'pin',
      icon: a.pinned ? PinOff : Pin,
      label: a.pinned ? 'Desafixar conversa' : 'Fixar conversa',
      onClick: a.onTogglePin,
    });
  }
  itens.push({ key: 'close', icon: MessageCircleX, label: 'Fechar', onClick: a.onClose, danger: true });
  return itens;
}
